import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Message } from '../../types/firestore'; // Keep Message type
import { messageService } from '../../services/messageService';
import { Button, Spinner, Alert, Modal } from '../common/UIComponents';
import { insertQuickReplyIntoDraft, type QuickReply } from '../../utils/chatQuickReplies';

// Import Lightbox
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import ImageUploadInput from '../shared/ImageUploadInput'; // Import ImageUploadInput
import {
  PaperAirplaneIcon,
  ExclamationCircleIcon,
  ChatBubbleLeftRightIcon,
  PaperClipIcon, // Icon for attaching files
  XMarkIcon, // Icon to close uploader/remove attachments
} from '@heroicons/react/24/outline';

interface ChatWindowProps {
  repairRequestId: string;
  receiverId: string; // ID of the other person in the chat
  receiverName?: string; // Optional display name of the receiver
  onUserEngaged?: () => void;
  onThreadReadyChange?: (isReady: boolean) => void;
  onLastIncomingMessageVisibilityChange?: (isVisible: boolean) => void;
  onMessageCountChange?: (count: number) => void;
  onComposerFocusChange?: (isFocused: boolean) => void;
  compactEmptyState?: boolean;
  quickReplies?: QuickReply[];
  initialBottomAnchorToken?: number;
  initialBottomAnchorReady?: boolean;
}

// Define attachment type based on firestore.ts
interface ChatAttachment {
  type: 'image' | 'file'; // Extend as needed
  url: string;
  name: string;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  repairRequestId,
  receiverId,
  onUserEngaged,
  onThreadReadyChange,
  onLastIncomingMessageVisibilityChange,
  onMessageCountChange,
  onComposerFocusChange,
  compactEmptyState = false,
  quickReplies = [],
  initialBottomAnchorToken,
  initialBottomAnchorReady = true,
}) => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [showImageUpload, setShowImageUpload] = useState(false); // Desktop inline uploader visibility
  const [isMobileUploadSheetOpen, setIsMobileUploadSheetOpen] = useState(false);
  const [stagedAttachments, setStagedAttachments] = useState<ChatAttachment[]>([]); // State for staged attachments
  const [isComposerFocused, setIsComposerFocused] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 767px)').matches
      : false
  );

  // State for Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const attachmentTriggerRef = useRef<HTMLElement | null>(null);
  const attachmentInitialFocusRef = useRef<HTMLButtonElement | null>(null);
  const lastIncomingMessageRef = useRef<HTMLDivElement | null>(null);
  const messagesContentRef = useRef<HTMLDivElement | null>(null);
  const userEngagedRef = useRef(false);
  const autoScrollInProgressRef = useRef(false);
  const stickToBottomRef = useRef(true);
  const pendingInitialAnchorTokenRef = useRef<number | null>(
    typeof initialBottomAnchorToken === 'number' ? initialBottomAnchorToken : null
  );
  const initialAnchorArmedRef = useRef(typeof initialBottomAnchorToken !== 'number');

  const getMessageDate = (message: Message): Date => {
    const createdAt = message.createdAt as any;
    if (createdAt && typeof createdAt.toDate === 'function') {
      return createdAt.toDate();
    }
    return new Date();
  };

  const isNearBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return false;
    const remainingDistance = container.scrollHeight - container.clientHeight - container.scrollTop;
    return remainingDistance <= 24;
  };

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    const container = messagesContainerRef.current;
    if (!container) return;
    autoScrollInProgressRef.current = true;
    container.scrollTo({
      top: container.scrollHeight,
      behavior,
    });
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        autoScrollInProgressRef.current = false;
      });
    });
  };

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleViewportChange = (event: MediaQueryListEvent) => {
      setIsMobileViewport(event.matches);
      if (!event.matches) {
        setIsMobileUploadSheetOpen(false);
      }
    };

    setIsMobileViewport(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleViewportChange);
      return () => mediaQuery.removeEventListener('change', handleViewportChange);
    }

    mediaQuery.addListener(handleViewportChange);
    return () => mediaQuery.removeListener(handleViewportChange);
  }, []);

  useEffect(() => {
    setShowImageUpload(false);
    setIsMobileUploadSheetOpen(false);
    onComposerFocusChange?.(false);
  }, [onComposerFocusChange, repairRequestId]);

  useEffect(() => {
    userEngagedRef.current = false;
    stickToBottomRef.current = true;
    pendingInitialAnchorTokenRef.current =
      typeof initialBottomAnchorToken === 'number' ? initialBottomAnchorToken : null;
    initialAnchorArmedRef.current = typeof initialBottomAnchorToken !== 'number';
  }, [initialBottomAnchorToken, repairRequestId]);

  useEffect(() => {
    return () => {
      onComposerFocusChange?.(false);
    };
  }, [onComposerFocusChange]);

  // Subscribe to messages
  useEffect(() => {
    if (!repairRequestId || !currentUser) {
      setError('Brak wymaganych danych do załadowania czatu.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = messageService.getMessagesSubscription(
      repairRequestId,
      (updatedMessages) => {
        setMessages(updatedMessages);
        setLoading(false);
      },
      (err) => {
        setError('Nie udało się załadować wiadomości.');
        setLoading(false);
        console.error(err);
      }
    );

    return () => {
      console.log(`Zakończono subskrypcję wiadomości dla ${repairRequestId}`);
      unsubscribe();
    };
  }, [repairRequestId, currentUser]);

  useEffect(() => {
    if (!loading && initialAnchorArmedRef.current && stickToBottomRef.current) {
      const behavior = messages.length <= 1 ? 'auto' : 'smooth';
      const raf = window.requestAnimationFrame(() => {
        scrollToBottom(behavior);
      });
      return () => window.cancelAnimationFrame(raf);
    }
  }, [messages, loading]);

  useEffect(() => {
    if (
      typeof initialBottomAnchorToken !== 'number' ||
      pendingInitialAnchorTokenRef.current === null ||
      loading ||
      !initialBottomAnchorReady
    ) {
      return;
    }

    stickToBottomRef.current = true;
    const nextToken = pendingInitialAnchorTokenRef.current;
    const raf = window.requestAnimationFrame(() => {
      scrollToBottom('auto');
      initialAnchorArmedRef.current = true;
      if (pendingInitialAnchorTokenRef.current === nextToken) {
        pendingInitialAnchorTokenRef.current = null;
      }
    });

    return () => window.cancelAnimationFrame(raf);
  }, [initialBottomAnchorReady, initialBottomAnchorToken, loading]);

  useEffect(() => {
    if (typeof initialBottomAnchorToken !== 'number') {
      return;
    }
    pendingInitialAnchorTokenRef.current = initialBottomAnchorToken;
    initialAnchorArmedRef.current = false;
    stickToBottomRef.current = true;
  }, [initialBottomAnchorToken]);

  useEffect(() => {
    if (!isMobileViewport || !isComposerFocused) {
      return;
    }
    if (document.activeElement !== inputRef.current) {
      return;
    }

    const syncViewportAfterKeyboardChange = () => {
      if (initialAnchorArmedRef.current && stickToBottomRef.current) {
        window.requestAnimationFrame(() => {
          scrollToBottom('auto');
        });
      }
    };

    const visualViewport = window.visualViewport;
    syncViewportAfterKeyboardChange();
    visualViewport?.addEventListener('resize', syncViewportAfterKeyboardChange);
    visualViewport?.addEventListener('scroll', syncViewportAfterKeyboardChange);

    return () => {
      visualViewport?.removeEventListener('resize', syncViewportAfterKeyboardChange);
      visualViewport?.removeEventListener('scroll', syncViewportAfterKeyboardChange);
    };
  }, [isComposerFocused, isMobileViewport]);

  useEffect(() => {
    if (!loading) {
      onMessageCountChange?.(messages.length);
    }
  }, [loading, messages.length, onMessageCountChange]);

  useEffect(() => {
    const isReady = Boolean(
      !loading && !error && currentUser?.uid && repairRequestId && receiverId
    );
    onThreadReadyChange?.(isReady);
  }, [currentUser?.uid, error, loading, onThreadReadyChange, receiverId, repairRequestId]);

  const markUserEngaged = () => {
    if (!userEngagedRef.current) {
      userEngagedRef.current = true;
      onUserEngaged?.();
    } else {
      onUserEngaged?.();
    }
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    const target = lastIncomingMessageRef.current;
    if (!container || !target || !onLastIncomingMessageVisibilityChange) {
      onLastIncomingMessageVisibilityChange?.(false);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const isVisible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.6);
        onLastIncomingMessageVisibilityChange(isVisible);
      },
      { root: container, threshold: [0, 0.6] }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [messages.length, loading, onLastIncomingMessageVisibilityChange]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    const content = messagesContentRef.current;
    if (
      !container ||
      !content ||
      loading ||
      typeof ResizeObserver === 'undefined' ||
      !initialAnchorArmedRef.current
    ) {
      return;
    }

    const observer = new ResizeObserver(() => {
      if (stickToBottomRef.current) {
        scrollToBottom('auto');
      }
    });

    observer.observe(content);
    observer.observe(container);
    return () => observer.disconnect();
  }, [loading, messages.length]);

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(event.target.value);
  };

  const handleInsertQuickReply = (quickReply: QuickReply) => {
    if (!currentUser || loading || sending) {
      return;
    }

    markUserEngaged();
    setNewMessage((currentDraft) => insertQuickReplyIntoDraft(currentDraft, quickReply));
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      if (inputRef.current) {
        inputRef.current.selectionStart = inputRef.current.value.length;
        inputRef.current.selectionEnd = inputRef.current.value.length;
      }
    });
  };

  const handleSend = async (event?: React.FormEvent) => {
    event?.preventDefault();
    // Allow sending only attachments without text
    if (!newMessage.trim() && stagedAttachments.length === 0) return;
    if (!currentUser || !receiverId || sending) return;

    setSending(true);
    setError(null);
    const messageToSend = newMessage.trim();
    const attachmentsToSend = [...stagedAttachments]; // Copy staged attachments

    // Clear input and staged files immediately
    setNewMessage('');
    setStagedAttachments([]);
    setShowImageUpload(false);
    setIsMobileUploadSheetOpen(false);

    try {
      // Ensure messageService.sendMessage can handle attachments
      await messageService.sendMessage(
        repairRequestId,
        currentUser.uid,
        receiverId,
        messageToSend, // Send text content
        attachmentsToSend // Send attachments
      );
      // Message appears via subscription
    } catch (err) {
      console.error('Błąd wysyłania wiadomości:', err);
      setError('Nie udało się wysłać wiadomości.');
      // Restore message and attachments on error
      setNewMessage(messageToSend);
      setStagedAttachments(attachmentsToSend);
      if (!isMobileViewport) {
        setShowImageUpload(attachmentsToSend.length > 0);
      }
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleUploadComplete = (urls: string[]) => {
    const newAttachments: ChatAttachment[] = urls.map((url) => {
      // Try to extract a meaningful name, fallback to timestamped name
      const name = url.split('/').pop()?.split('?')[0]?.split('%2F').pop() || `image-${Date.now()}`;
      return { type: 'image', url, name: decodeURIComponent(name) };
    });
    setStagedAttachments((prev) => {
      const existingUrls = new Set(prev.map((attachment) => attachment.url));
      const deduplicated = newAttachments.filter((attachment) => !existingUrls.has(attachment.url));
      if (deduplicated.length === 0) {
        return prev;
      }
      return [...prev, ...deduplicated];
    });
    if (isMobileViewport) {
      setIsMobileUploadSheetOpen(false);
    }
  };

  const toggleAttachmentPicker = (event?: React.MouseEvent<HTMLButtonElement>) => {
    if (isMobileViewport) {
      attachmentTriggerRef.current = event?.currentTarget || null;
      setIsComposerFocused(false);
      onComposerFocusChange?.(false);
      setIsMobileUploadSheetOpen(true);
      return;
    }
    setShowImageUpload((prev) => !prev);
  };

  const closeMobileUploadSheet = () => setIsMobileUploadSheetOpen(false);

  const removeStagedAttachment = (index: number) => {
    setStagedAttachments((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  // Format time for messages
  const formatMessageTime = (timestamp: any): string => {
    if (!timestamp || typeof timestamp.toDate !== 'function') return '';
    try {
      return timestamp.toDate().toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      console.error('Error formatting time:', e);
      return '--:--';
    }
  };

  // Group messages by date
  const groupMessagesByDate = (messages: Message[]) => {
    const groups: { [key: string]: Message[] } = {};
    messages.forEach((message) => {
      const messageDate = getMessageDate(message);
      const dateKey = messageDate.toLocaleDateString('pl-PL');
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(message);
    });
    return Object.entries(groups)
      .map(([, msgs]) => ({
        date: getMessageDate(msgs[0]),
        messages: msgs,
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  };

  // Get label for date group divider
  const getGroupDateLabel = (date: Date): string => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Dzisiaj';
    if (date.toDateString() === yesterday.toDateString()) return 'Wczoraj';
    // Corrected options object
    return date.toLocaleDateString('pl-PL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  }; // Correctly closed function

  const messageGroups = groupMessagesByDate(messages);

  // Create a flat list of all image URLs for the lightbox
  const allImageUrls = messages.flatMap(
    (msg) => msg.attachments?.filter((att) => att.type === 'image').map((att) => att.url) || []
  );

  // Function to open lightbox for a specific image URL
  const openLightbox = (imageUrl: string) => {
    const index = allImageUrls.findIndex((url) => url === imageUrl);
    if (index !== -1) {
      setLightboxIndex(index);
      setLightboxOpen(true);
    }
  };

  const lastIncomingMessageId = (() => {
    if (!currentUser) return null;
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message.senderId && message.senderId !== currentUser.uid) {
        return message.id ?? message.uid;
      }
    }
    return null;
  })();

  const mobileUploadSheetPortal =
    isMobileViewport && isMobileUploadSheetOpen && typeof document !== 'undefined'
      ? createPortal(
          <Modal
            isOpen
            onClose={closeMobileUploadSheet}
            ariaLabel="Panel dodawania załączników"
            initialFocusRef={attachmentInitialFocusRef}
            returnFocusRef={attachmentTriggerRef}
            placement="bottom-responsive"
            overlayClassName="!z-[120] !bg-black/55 md:hidden"
            overlayTestId="chat-mobile-upload-overlay"
            dialogTestId="mobile-upload-sheet"
            className="flex !max-h-[min(72dvh,30rem)] !w-full !max-w-3xl flex-col !overflow-hidden !rounded-b-none !rounded-t-2xl !border-x-0 !border-b-0 !border-t-primary-dark-300 !bg-primary-dark-100 !shadow-2xl"
          >
            <div className="flex items-center justify-between gap-3 border-b border-primary-dark-300 px-4 py-3">
              <p className="text-sm font-semibold text-primary-light-100">Dodaj załączniki</p>
              <div className="flex items-center gap-2">
                <button
                  ref={attachmentInitialFocusRef}
                  type="button"
                  onClick={closeMobileUploadSheet}
                  className="text-xs text-primary-light-300 hover:text-primary-light-100 focus-outline"
                >
                  Zamknij
                </button>
                <button
                  type="button"
                  onClick={closeMobileUploadSheet}
                  className="rounded-md p-1 text-primary-light-300 hover:text-primary-light-100 focus-outline"
                  aria-label="Zamknij"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="min-h-0 overflow-y-auto overscroll-contain p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
              <ImageUploadInput
                maxFiles={3}
                storagePath={`chat-attachments/${repairRequestId}`}
                onUploadComplete={handleUploadComplete}
                label="Dodaj zdjęcia (max 3)"
                autoUpload
                compactLayout
              />
            </div>
          </Modal>,
          document.body
        )
      : null;

  return (
    <>
      <div
        className="relative flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-transparent"
        onClickCapture={markUserEngaged}
        onFocusCapture={markUserEngaged}
        data-testid="chat-window-root"
      >
        {/* Messages container */}
        <div
          ref={messagesContainerRef}
          className="min-h-0 flex-1 space-y-2 overflow-y-auto overflow-x-hidden overscroll-contain p-2.5 sm:p-3 custom-scrollbar [touch-action:pan-y]"
          onClick={markUserEngaged}
          onFocus={markUserEngaged}
          onMouseDown={markUserEngaged}
          onWheel={markUserEngaged}
          onTouchStart={markUserEngaged}
          onScroll={() => {
            if (!autoScrollInProgressRef.current) {
              stickToBottomRef.current = isNearBottom();
            }
            if (userEngagedRef.current) {
              onUserEngaged?.();
            }
          }}
          data-testid="chat-messages-container"
          data-chat-scroll-owner="true"
          tabIndex={0}
        >
          <div ref={messagesContentRef} className="min-h-full" data-testid="chat-messages-content">
            {loading && (
              <div className="flex h-full items-center justify-center">
                <Spinner color="primary" size="lg" />
                <span className="ml-3 text-primary-light-300">Ładowanie wiadomości...</span>
              </div>
            )}

            {error && !loading && (
              <Alert
                variant="error"
                icon={<ExclamationCircleIcon className="w-5 h-5" />}
                className="m-4"
              >
                {error}
              </Alert>
            )}

            {!loading && messages.length === 0 && !error && (
              <div
                className={`flex min-h-full min-w-0 flex-col items-center justify-center px-3 text-center ${
                  compactEmptyState ? 'py-3' : 'py-6'
                }`}
              >
                <ChatBubbleLeftRightIcon
                  className={`${compactEmptyState ? 'w-7 h-7 mb-1.5' : 'w-10 h-10 mb-3'} text-primary-light-400`}
                />
                <p className="mb-1 max-w-full break-words text-sm font-medium text-primary-light-300">
                  Brak wiadomości
                </p>
                <p className="max-w-[22rem] text-balance break-words text-sm leading-relaxed text-primary-light-400">
                  {compactEmptyState
                    ? 'Napisz krótką wiadomość, aby rozpocząć ustalenia.'
                    : 'Rozpocznij rozmowę, aby doprecyzować szczegóły sprawy.'}
                </p>
              </div>
            )}

            {!loading &&
              messageGroups.map((group, groupIndex) => (
                <React.Fragment key={groupIndex}>
                  {/* Date divider */}
                  <div className="relative flex py-2 items-center justify-center my-2">
                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                      <div className="w-full border-t border-primary-dark-300/30"></div>
                    </div>
                    <div className="relative flex px-3 py-0.5 rounded-full bg-primary-dark-200/70 text-xs font-medium text-primary-light-400">
                      {getGroupDateLabel(group.date)}
                    </div>
                  </div>

                  {/* Messages in this group */}
                  <div className="space-y-2">
                    {group.messages.map((msg) => {
                      const isMine = msg.senderId === currentUser?.uid;
                      const isLastIncoming =
                        !isMine && (msg.id ?? msg.uid) === lastIncomingMessageId;
                      return (
                        <div
                          key={msg.id ?? msg.uid}
                          ref={isLastIncoming ? lastIncomingMessageRef : null}
                          className={`flex items-end gap-2 ${isMine ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`flex max-w-[min(75%,18rem)] min-w-0 flex-col sm:max-w-[75%] ${isMine ? 'items-end' : 'items-start'}`}
                          >
                            <div
                              className={`
                            max-w-full overflow-hidden rounded-xl px-3 py-2 shadow-sm
                            ${
                              isMine
                                ? 'bg-ratuj-500 text-white rounded-br-none'
                                : 'bg-primary-dark-300 text-primary-light-100 rounded-bl-none'
                            }
                        `}
                            >
                              {/* Display text content */}
                              {msg.content && (
                                <p className="text-sm break-words whitespace-pre-wrap">
                                  {msg.content}
                                </p>
                              )}
                              {/* Display attachments */}
                              {msg.attachments && msg.attachments.length > 0 && (
                                <div
                                  className={`mt-2 space-y-1 ${msg.content ? 'pt-2 border-t border-white/20' : ''}`}
                                >
                                  {msg.attachments.map((att, index) =>
                                    att.type === 'image' ? (
                                      <button
                                        key={index}
                                        onClick={() => openLightbox(att.url)}
                                        className="block focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-primary-dark-300 focus:ring-ratuj-300 rounded"
                                        aria-label={`Pokaż zdjęcie ${att.name || index + 1}`}
                                      >
                                        <img
                                          src={att.url}
                                          alt={att.name || 'Załączony obraz'}
                                          className="max-h-44 max-w-[min(16rem,68vw)] rounded border border-primary-dark-400 object-cover transition-opacity hover:opacity-80 sm:max-h-48 sm:max-w-xs"
                                        />
                                      </button>
                                    ) : (
                                      <a
                                        key={index}
                                        href={att.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-blue-300 hover:underline"
                                      >
                                        {att.name || 'Załączony plik'}
                                      </a>
                                    )
                                  )}
                                </div>
                              )}
                            </div>
                            <span
                              className={`block text-xs mt-1 ${isMine ? 'text-primary-light-400/80' : 'text-primary-light-400'}`}
                            >
                              {formatMessageTime(msg.createdAt)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </React.Fragment>
              ))}
          </div>
        </div>

        {/* Input Area */}
        <div
          className="sticky bottom-0 z-10 flex-shrink-0 overflow-x-hidden border-t border-primary-dark-300/16 bg-primary-dark-100/95 shadow-[0_-8px_18px_rgba(0,0,0,0.12)] backdrop-blur"
          style={{ paddingBottom: isMobileViewport ? 'env(safe-area-inset-bottom)' : undefined }}
          data-testid="chat-composer-shell"
          data-product-feedback-trigger-obstruction={
            stagedAttachments.length > 0 ||
            quickReplies.length > 0 ||
            (showImageUpload && !isMobileViewport)
              ? 'true'
              : undefined
          }
        >
          {stagedAttachments.length > 0 && (
            <div
              className="border-b border-primary-dark-300/30 bg-primary-dark-100/30 px-2.5 pb-2 pt-1.5 sm:px-3"
              data-testid="chat-attachment-strip"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-primary-light-300">
                  Załączniki do wysłania ({stagedAttachments.length})
                </p>
                {isMobileViewport && (
                  <button
                    type="button"
                    className="text-xs text-ratuj-300 hover:text-ratuj-200"
                    onClick={toggleAttachmentPicker}
                  >
                    Zmień wybór
                  </button>
                )}
              </div>
              <div className="mt-2 flex items-center gap-2 overflow-x-auto pb-1">
                {stagedAttachments.map((att, index) => (
                  <div
                    key={`${att.url}-${index}`}
                    className="relative w-12 h-12 flex-shrink-0 rounded border border-primary-dark-300"
                  >
                    <img
                      src={att.url}
                      alt={att.name}
                      className="w-full h-full object-cover rounded"
                    />
                    <button
                      type="button"
                      onClick={() => removeStagedAttachment(index)}
                      className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5 text-white hover:bg-red-600"
                      aria-label={`Usuń załącznik ${att.name}`}
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Desktop inline uploader */}
          {showImageUpload && !isMobileViewport && (
            <div className="border-b border-primary-dark-300/30 p-2.5 sm:p-3">
              <ImageUploadInput
                maxFiles={3}
                storagePath={`chat-attachments/${repairRequestId}`}
                onUploadComplete={handleUploadComplete}
                label="Dodaj zdjęcia (max 3)"
              />
            </div>
          )}

          {quickReplies.length > 0 && (
            <div
              className="flex-shrink-0 border-b border-primary-dark-300/16 px-2.5 pb-1 pt-1.5 sm:px-3"
              data-testid="chat-quick-reply-rail"
            >
              <div className="flex max-h-10 min-w-0 snap-x snap-proximity items-stretch gap-1.5 overflow-x-auto overflow-y-hidden pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {quickReplies.map((quickReply) => (
                  <button
                    key={quickReply.id}
                    type="button"
                    onClick={() => handleInsertQuickReply(quickReply)}
                    disabled={!currentUser || loading || sending}
                    className="h-9 w-[clamp(6.75rem,30vw,8rem)] min-w-0 flex-none snap-start overflow-hidden rounded-md border border-transparent bg-primary-dark-100/42 px-2.5 py-1 text-left transition-colors hover:bg-primary-dark-200/48 focus:outline-none focus:ring-2 focus:ring-ratuj-300/60 disabled:cursor-not-allowed disabled:opacity-50 sm:w-[13rem]"
                    aria-label={`Wstaw szybką odpowiedź: ${quickReply.label}`}
                  >
                    <span className="block truncate text-[11px] font-semibold leading-4 text-primary-light-100">
                      {quickReply.label}
                    </span>
                    <span className="block truncate text-[11px] leading-4 text-primary-light-400">
                      {quickReply.preview}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Message input form */}
          <form
            onSubmit={handleSend}
            className="flex min-w-0 items-center gap-2 p-2 sm:p-3"
            data-testid="chat-composer-form"
          >
            {/* Attach Button */}
            <Button
              type="button"
              variant="neutral"
              onClick={toggleAttachmentPicker}
              className="h-10 w-10 min-w-[2.5rem] flex-shrink-0 rounded-lg !p-0"
              aria-label={isMobileViewport ? 'Dodaj załączniki' : 'Dołącz zdjęcia'}
              disabled={sending}
            >
              {showImageUpload && !isMobileViewport ? (
                <XMarkIcon className="w-5 h-5" />
              ) : (
                <PaperClipIcon className="w-5 h-5" />
              )}
            </Button>

            <div className="min-w-0 flex-1">
              <textarea
                ref={inputRef}
                value={newMessage}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                onFocus={() => {
                  setIsComposerFocused(true);
                  onComposerFocusChange?.(true);
                  markUserEngaged();
                }}
                onBlur={() => {
                  setIsComposerFocused(false);
                  onComposerFocusChange?.(false);
                }}
                placeholder="Wpisz wiadomość..."
                rows={1}
                className="form-input min-h-[2.5rem] max-h-24 min-w-0 w-full resize-none !py-2 text-sm leading-5"
                disabled={!currentUser || loading || sending}
                aria-label="Wiadomość"
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              disabled={
                (!newMessage.trim() && stagedAttachments.length === 0) ||
                !currentUser ||
                loading ||
                sending
              }
              className="h-10 w-10 min-w-[2.5rem] flex-shrink-0 rounded-lg !p-0"
              aria-label="Wyślij wiadomość"
              data-testid="chat-send-button"
            >
              {sending ? (
                <Spinner size="sm" color="white" />
              ) : (
                <PaperAirplaneIcon className="w-5 h-5" />
              )}
            </Button>
          </form>
        </div>

        {/* Lightbox Component */}
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          slides={allImageUrls.map((url) => ({ src: url }))}
          index={lightboxIndex}
        />
      </div>
      {mobileUploadSheetPortal}
    </>
  );
};

export default ChatWindow;
